import { getCurrentScope, getIsolationScope } from "@sentry/core";
import { setActiveAppTag } from "./SentryDependencies";
import { installGlobalHandlers } from "./globalHandlers";
// Kept separate from the exported singleton so tests can build an isolated registry per case.
const createActiveClientRegistry = (installHandlers = installGlobalHandlers) => {
    const stack = [];
    let globalHandlersInstalled = false;
    // Last client bound as current; distinguishes an actual app change from an idempotent re-bind.
    let boundCurrentClient;
    // Claimed once by the first capable logger, never reclaimed — re-init to a second DSN would flip
    // the shared native `enableNative` flag.
    let hasNativeOwner = false;
    // The host app (user-area-front). Global unhandled errors are dual-sent here.
    let hostClient;
    const active = () => stack[stack.length - 1];
    /** Nearest client (from the top) that can capture — poison-top fallback. */
    const capableClient = () => {
        for (let index = stack.length - 1; index >= 0; index -= 1) {
            if (stack[index].canCapture()) {
                return stack[index];
            }
        }
        return undefined;
    };
    /**
     * Sentry skips any exception already stamped `__sentry_captured__`, so the stamp must be cleared
     * BEFORE each of our own captures to keep a re-captured or dual-sent instance reportable. Never
     * cleared afterwards: the leftover stamp still guards capture paths this registry does not own.
     */
    const clearAlreadyCapturedStamp = (error) => {
        delete error.__sentry_captured__;
    };
    const capture = (error, hint, tags) => {
        clearAlreadyCapturedStamp(error);
        return capableClient()?.captureException(error, hint, tags);
    };
    /** Project id of the active (capable) client, so a host can resolve which app owns capture. */
    const activeProjectId = () => capableClient()?.projectId?.();
    // Global unhandled errors are dual-sent to the active (capable) top AND the host, deduped when the
    // host IS the active top.
    const dispatch = (error, hint) => {
        const targets = new Set();
        const top = capableClient();
        if (top) {
            targets.add(top);
        }
        if (hostClient?.canCapture()) {
            targets.add(hostClient);
        }
        targets.forEach((target) => {
            // The SAME error object goes to every target, so the stamp must be cleared before each one.
            clearAlreadyCapturedStamp(error);
            target.captureException(error, hint);
        });
    };
    /**
     * Binds the global current client to the active (capable) top after every stack mutation, so
     * breadcrumbs/tracing follow the active app. Breadcrumbs live on the single shared isolation scope,
     * so an actual app change must clear them first or app A's event carries app B's crumbs.
     */
    const bindCurrentToActiveTop = () => {
        const next = capableClient();
        if (!next) {
            getCurrentScope().setClient(undefined);
            getCurrentScope().setUser(null);
            getIsolationScope().clearBreadcrumbs();
            // Record on the native scope that no micro-app is active (no-op on web).
            if (hasNativeOwner) {
                setActiveAppTag(undefined);
            }
            boundCurrentClient = undefined;
            return;
        }
        if (next !== boundCurrentClient) {
            getIsolationScope().clearBreadcrumbs();
        }
        next.setAsCurrent();
        // A native crash always ships on the owner's DSN; this tag records which micro-app was active.
        if (hasNativeOwner) {
            setActiveAppTag(next.projectId?.());
        }
        boundCurrentClient = next;
    };
    // Flushes the same targets a global crash is dual-sent to, so a fatal crash doesn't terminate the
    // app before the host's copy ships. Resolves true only if every target flushed.
    const flush = (timeout) => {
        const targets = new Set();
        const top = capableClient();
        if (top) {
            targets.add(top);
        }
        if (hostClient?.canCapture()) {
            targets.add(hostClient);
        }
        if (targets.size === 0) {
            return Promise.resolve(false);
        }
        return Promise.all([...targets].map((target) => target.flush(timeout))).then((results) => results.every(Boolean));
    };
    const ensureGlobalHandlersInstalled = () => {
        if (globalHandlersInstalled) {
            return;
        }
        globalHandlersInstalled = true;
        installHandlers(dispatch, flush);
    };
    const register = (client, { role = "screen", isHost = false } = {}) => {
        ensureGlobalHandlersInstalled();
        if (isHost) {
            hostClient = client;
        }
        const isScreen = role === "screen";
        // Offered only if unclaimed; a passive provider never owns the native SDK.
        const claimsNativeOwnership = !hasNativeOwner && isScreen;
        // Always build the client so its own scoped captures (ErrorBoundary / sendException) work, even
        // for a passive provider — which, never joining the stack, never binds itself as current.
        client.activate({ claimsNativeOwnership, becomesCurrent: isScreen });
        // Only actually taken if the client came up capable.
        if (claimsNativeOwnership && client.canCapture()) {
            hasNativeOwner = true;
        }
        // Only foreground screens join the active stack; a passive provider must never become current.
        if (isScreen) {
            stack.push(client);
            bindCurrentToActiveTop();
        }
        let unregistered = false;
        return () => {
            if (unregistered) {
                return;
            }
            unregistered = true;
            if (hostClient === client) {
                hostClient = undefined;
            }
            if (!isScreen) {
                return;
            }
            const index = stack.indexOf(client);
            if (index !== -1) {
                // Pop by identity; the client stays open (never closed) and `hasNativeOwner` is never reset.
                stack.splice(index, 1);
                bindCurrentToActiveTop();
            }
        };
    };
    return { register, active, capture, activeProjectId };
};
const activeClientRegistry = createActiveClientRegistry();
export { activeClientRegistry, createActiveClientRegistry };
